import { Box, Container, Grid, Hidden, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { useMediaUp } from 'hooks';
import useIsViewport, { TUseViewportProps } from 'hooks/useIsViewport';
import { ContentDescription } from 'components/structures/general';
import {
  macbookImage,
  phoneNoteImg1,
  phoneNoteImg1x2,
  phoneNoteImg2,
  phoneNoteImg2x2,
  screenImg1,
  screenImg2,
} from 'images/retail-energy';
import React, { FC, useState } from 'react';

const useStyles = makeStyles((theme: Theme) => ({
  titleText: {
    maxWidth: 500,
  },
  laptopImage: {
    maxWidth: '100%',
  },
  slideImage: {
    position: 'absolute',
    objectFit: 'cover',
    width: 338,
    height: 212,
    top: 16,
    left: 56,
    [theme.breakpoints.up('lg')]: {
      width: 457,
      height: 284,
      top: 22,
      left: 77,
    },
  },
  mobileImage: {
    width: '100%',
    marginBottom: theme.spacing(9),
  },
  spacer: {
    paddingTop: theme.spacing(10),
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(50),
    },
    [theme.breakpoints.up('lg')]: {
      paddingTop: theme.spacing(80),
    },
  },
  fadeIn: { animation: '$fade-in 600ms forwards' },
  fadeOut: { animation: '$fade-out 600ms forwards' },
  '@keyframes fade-in': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  '@keyframes fade-out': {
    from: { opacity: 1 },
    to: { opacity: 0 },
  },
}));

interface EnergySupplierProps {
  className?: string;
}

const EnergySupplier: FC<EnergySupplierProps> = ({ className }) => {
  const classes = useStyles();
  const isMedium = useMediaUp('md');
  const [inViewPort, setInViewPort] = useState<number>(1);
  const slideImages: string[] = [screenImg1, screenImg2];
  return (
    <Container fixed>
      <Box className={className}>
        <Grid container spacing={isMedium ? 6 : 0}>
          <Grid item md={6} xs={12}>
            <TextDescription
              mobileImages={{ x1: phoneNoteImg1, x2: phoneNoteImg1x2 }}
              viewPortHandler={{ index: 1, setInViewPort, viewportParams: { rootMargin: '-400px' } }}
              title="Switch your energy supplier"
              description="By simply entering your ZIP Code, Customer Type, Rate Class data, as well as Annual Usage, you get access to the list of the best local Energy Suppliers on the market. That way, you can benefit from the best deals out there and switch your Energy Supplier literally on the go. The process takes a few minutes of your time – it's fast and easy."
            />
            <Box className={classes.spacer} />
            <TextDescription
              mobileImages={{ x1: phoneNoteImg2, x2: phoneNoteImg2x2 }}
              viewPortHandler={{ index: 2, setInViewPort, viewportParams: { rootMargin: '-400px' } }}
              title="Choose the best rate"
              description="Based on user input, our platform automatically offers wholly customizable and highly accurate energy plans. This means that Energy5 allows you to compare Energy Providers, and when you've found the best rate, start the process of switching suppliers online. Switching to a better rate means fewer unfounded expenses and greater savings."
            />
          </Grid>
          <Hidden smDown>
            <Grid item md={6}>
              <Box position="sticky" top="15%" left="0">
                <Box position="relative">
                  <img src={macbookImage} className={classes.laptopImage} alt="laptop" />
                  {slideImages.map((image, index) => (
                    <img
                      src={image}
                      alt={image + index}
                      key={image + index}
                      style={{ zIndex: index + 2 }}
                      className={clsx(classes.slideImage, index < inViewPort ? classes.fadeIn : classes.fadeOut)}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Hidden>
        </Grid>
      </Box>
    </Container>
  );
};

interface TextDescriptionProps {
  title: string;
  description: string;
  mobileImages: { x1: string; x2: string };
  viewPortHandler: TUseViewportProps;
}

const TextDescription: FC<TextDescriptionProps> = ({ title, description, viewPortHandler, mobileImages: { x1, x2 } }) => {
  const classes = useStyles();
  const { ref } = useIsViewport(viewPortHandler);

  return (
    <div ref={ref}>
      <Hidden mdUp>
        <img src={x1} srcSet={`${x1} 500w, ${x2} 1084w`} alt={x1} className={classes.mobileImage} />
      </Hidden>
      <ContentDescription title={{ text: title, variant: 'h1', className: classes.titleText }} text={{ text: description }} />
    </div>
  );
};

export default EnergySupplier;
